import React, {useCallback, useRef, useState} from "react";
import {SignInDialog} from "../../components/auth/SignInDialog";
import {useAuthState} from "./useAuthState";

/**
 * Wraps an action so it only runs for signed-in members.
 *
 * A guest gets the sign-in dialog in place, and the action they reached for is
 * replayed once they are through it, so a tap on "save park" is never silently
 * swallowed. While auth is still resolving the action is ignored rather than
 * treated as a guest — see useAuthState.
 *
 * Render {signInDialog} somewhere in the component that uses this.
 */
export function useRequireAuth() {
  const authState = useAuthState()
  const [prompting, setPrompting] = useState(false)
  const pendingAction = useRef<(() => void) | undefined>()

  const requireAuth = useCallback((action: () => void) => {
    if (authState === "signedIn") {
      action()
      return
    }
    if (authState === "loading") return

    pendingAction.current = action
    setPrompting(true)
  }, [authState])

  const close = useCallback(() => {
    pendingAction.current = undefined
    setPrompting(false)
  }, [])

  const onSignedIn = useCallback(() => {
    const action = pendingAction.current
    pendingAction.current = undefined
    setPrompting(false)
    action?.()
  }, [])

  const signInDialog = prompting
    ? <SignInDialog onClose={close} onSignedIn={onSignedIn}/>
    : null

  return {authState, requireAuth, signInDialog, promptSignIn: () => setPrompting(true)}
}
